// src/encounter/services/use-referral-search.ts
// In-dialog search for the A/P referral picker's "additional / other diagnoses" field
// (PROD-1297). The referral dialog lets a provider attach extra diagnoses to a referral as
// removable chips; those come from the SAME master CPT/diagnosis catalog the procedure picker
// uses — GET /api/Master/ProcedureCodesList?searchQuery= → CodeDisplayName[] — mapped to DS
// CodeSearchResult[] via the existing procedureOptionsToCodeSearchResults helper.
//
// (The referral TARGET itself is contract-uncertain and is supplied by the caller in v1; this
// hook only powers the additional-diagnoses chip search.)
//
// Stale-request safety mirrors the sibling master searches (useProcedureCodeSearch): signal
// forwarded, enabled gating below 2 chars, query-key cancellation, placeholderData retention.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { procedureOptionsToCodeSearchResults } from '../lib/to-code-search-result';
import type { CodeDisplayName } from '@/types/patient-chart-api';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';

const MIN_QUERY_LENGTH = 2;

export function useReferralSearch(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery<CodeSearchResult[]>({
    queryKey: ['referral-additional-diagnoses', trimmed],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      const res = await get<CodeDisplayName[]>(
        `/api/Master/ProcedureCodesList?${qs.toString()}`,
        { signal },
      );
      return procedureOptionsToCodeSearchResults(res ?? []);
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
